import React from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { MudType, PhaseOleosa, Densificante } from '../../types';
import {
  mudTypeFormFields,
  phaseOleosaFormFields,
  densificanteFormFields
} from '../../config/formFields';

interface MudSectionProps {
  data: {
    mudTypes: MudType[];
    phaseOleosa: PhaseOleosa[];
    densificantes: Densificante[];
  };
  operations: {
    handleMudTypeSubmit: (data: Record<string, any>) => Promise<void>;
    handleMudTypeToggle: (item: MudType) => Promise<void>;
    handleMudTypeDelete: (item: MudType) => Promise<void>;
    handleMudTypeEdit: (item: MudType, key: string, newValue: any) => Promise<void>;
    handlePhaseOleosaSubmit: (data: Record<string, any>) => Promise<void>;
    handlePhaseOleosaToggle: (item: PhaseOleosa) => Promise<void>;
    handlePhaseOleosaDelete: (item: PhaseOleosa) => Promise<void>;
    handlePhaseOleosaEdit: (item: PhaseOleosa, key: string, newValue: any) => Promise<void>;
    handleDensificanteSubmit: (data: Record<string, any>) => Promise<void>;
    handleDensificanteToggle: (item: Densificante) => Promise<void>;
    handleDensificanteDelete: (item: Densificante) => Promise<void>;
    handleDensificanteEdit: (item: Densificante, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const MudSection: React.FC<MudSectionProps> = ({ data, operations, userPermissions }) => {
  // Table column configurations
  const mudTypeColumns: TableColumn<MudType>[] = [
    {
      key: 'type',
      title: 'Tipo',
      width: '25%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleMudTypeEdit
    },
    {
      key: 'baseMudType',
      title: 'Base',
      width: '15%',
      editable: true,
      editType: 'select',
      selectOptions: [
        { value: 'WBM', label: 'WBM (Base Agua)' },
        { value: 'OBM', label: 'OBM (Base Aceite)' },
        { value: 'SBM', label: 'SBM (Base Sintético)' }
      ],
      onEdit: operations.handleMudTypeEdit
    },
    {
      key: 'description',
      title: 'Descripción',
      width: '35%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleMudTypeEdit,
      render: (value) => value || '(Sin descripción)'
    },
    {
      key: 'density',
      title: 'Densidad (g/l)',
      width: '15%',
      editable: true,
      editType: 'number',
      onEdit: operations.handleMudTypeEdit,
      render: (value) => value ? `${value} g/l` : '(Sin definir)'
    }
  ];

  const phaseOleosaColumns: TableColumn<PhaseOleosa>[] = [
    {
      key: 'name',
      title: 'Nombre',
      width: '30%',
      editable: true,
      editType: 'text',
      onEdit: operations.handlePhaseOleosaEdit
    },
    {
      key: 'density',
      title: 'Densidad (g/l)',
      width: '20%',
      editable: true,
      editType: 'number',
      onEdit: operations.handlePhaseOleosaEdit,
      render: (value) => `${value} g/l`
    },
    {
      key: 'cost',
      title: 'Costo (USD/bbl)',
      width: '20%',
      editable: true,
      editType: 'number',
      onEdit: operations.handlePhaseOleosaEdit,
      render: (value) => value ? `$${value.toFixed(2)}` : '$0.00'
    }
  ];

  const densificanteColumns: TableColumn<Densificante>[] = [
    {
      key: 'name',
      title: 'Nombre',
      width: '30%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleDensificanteEdit
    },
    {
      key: 'density',
      title: 'Densidad (g/l)',
      width: '20%',
      editable: true,
      editType: 'number',
      onEdit: operations.handleDensificanteEdit,
      render: (value) => `${value} g/l`
    },
    {
      key: 'cost',
      title: 'Costo (USD/ton)',
      width: '20%',
      editable: true,
      editType: 'number',
      onEdit: operations.handleDensificanteEdit,
      render: (value) => value ? `$${value.toFixed(2)}` : '$0.00'
    }
  ];

  // Quick Setup Handlers
  const handleMudTypesQuickSetup = async () => {
    if (window.confirm('¿Cargar tipos de lodo estándar? Esta acción agregará múltiples registros.')) {
      const standardMudTypes = [
        // Water Based Muds
        { type: 'Bentonítico Básico', baseMudType: 'WBM', description: 'Sistema básico con bentonita y polímeros', density: 1080 },
        { type: 'KCl-Polímero', baseMudType: 'WBM', description: 'Sistema inhibido con cloruro de potasio', density: 1150 },
        { type: 'Calcio-Cloruro', baseMudType: 'WBM', description: 'Sistema de calcio para formaciones sensibles', density: 1200 },

        // Oil Based Muds
        { type: 'Diesel-Emulsión Invertida', baseMudType: 'OBM', description: 'Sistema de emulsión invertida con diesel', density: 1400 },
        { type: 'Mineral Oil-Emulsión', baseMudType: 'OBM', description: 'Base aceite mineral para formaciones reactivas', density: 1350 },

        // Synthetic Based Muds
        { type: 'Sintético-Éster', baseMudType: 'SBM', description: 'Base sintética con ésteres para alto rendimiento', density: 1300 },
        { type: 'Sintético-Olefinas', baseMudType: 'SBM', description: 'Sistema sintético con alfa-olefinas', density: 1280 }
      ];

      try {
        for (const mudType of standardMudTypes) {
          await operations.handleMudTypeSubmit({
            ...mudType,
            active: true
          });
        }
        alert('✅ Tipos de lodo estándar cargados exitosamente');
      } catch (error) {
        console.error('Error cargando tipos de lodo:', error);
        alert('❌ Error cargando tipos de lodo. Ver consola para detalles.');
      }
    }
  };

  const handlePhaseOleosaQuickSetup = async () => {
    if (window.confirm('¿Cargar fases oleosas estándar? Esta acción agregará múltiples registros.')) {
      const standardPhases = [
        { name: 'Diesel #2', density: 850, cost: 120.00 },
        { name: 'Aceite Mineral Parafínico', density: 880, cost: 95.00 },
        { name: 'Aceite Base Nafténico', density: 900, cost: 110.00 },
        { name: 'Sintético Éster', density: 820, cost: 280.00 },
        { name: 'Alfa-Olefinas Internas', density: 810, cost: 320.00 }
      ];

      try {
        for (const phase of standardPhases) {
          await operations.handlePhaseOleosaSubmit({
            ...phase,
            active: true
          });
        }
        alert('✅ Fases oleosas estándar cargadas exitosamente');
      } catch (error) {
        console.error('Error cargando fases oleosas:', error);
        alert('❌ Error cargando fases oleosas. Ver consola para detalles.');
      }
    }
  };

  const handleDensificantesQuickSetup = async () => {
    if (window.confirm('¿Cargar densificantes estándar? Esta acción agregará múltiples registros.')) {
      const standardDensificantes = [
        { name: 'Baritina', density: 4200, cost: 180.00 },
        { name: 'Calcita', density: 2710, cost: 45.00 },
        { name: 'Hematita', density: 5020, cost: 250.00 },
        { name: 'Ilmenita', density: 4700, cost: 320.00 },
        { name: 'Carbonato de Calcio', density: 2700, cost: 65.00 }
      ];

      try {
        for (const densificante of standardDensificantes) {
          await operations.handleDensificanteSubmit({
            ...densificante,
            active: true
          });
        }
        alert('✅ Densificantes estándar cargados exitosamente');
      } catch (error) {
        console.error('Error cargando densificantes:', error);
        alert('❌ Error cargando densificantes. Ver consola para detalles.');
      }
    }
  };

  // Master Quick Setup
  const handleAllMudDataQuickSetup = async () => {
    if (window.confirm('¿Cargar toda la información de lodos con datos estándar? Esta acción agregará múltiples registros.')) {
      try {
        await handleMudTypesQuickSetup();
        await handlePhaseOleosaQuickSetup();
        await handleDensificantesQuickSetup();
        alert('✅ Sistema de lodos configurado exitosamente con todos los datos estándar');
      } catch (error) {
        console.error('Error en configuración completa de lodos:', error);
        alert('❌ Error durante la configuración. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>🧪 Gestión de Lodos y Fluidos</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra tipos de lodo, fases oleosas, y densificantes utilizados en las operaciones de perforación
        </p>
      </div>

      {/* Mud Types Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Tipos de Lodo',
          '🧪',
          data.mudTypes,
          mudTypeFormFields,
          mudTypeColumns,
          operations.handleMudTypeSubmit,
          operations.handleMudTypeToggle,
          operations.handleMudTypeDelete,
          {
            description: 'Define los tipos de lodo de perforación utilizados: base agua (WBM), base aceite (OBM) y base sintético (SBM)',
            quickSetup: {
              title: 'Carga Rápida de Tipos de Lodo',
              description: 'Carga tipos estándar: bentonítico, KCl-polímero, emulsión invertida, sintéticos',
              buttonLabel: '🧪 Cargar Tipos Estándar',
              onQuickSetup: handleMudTypesQuickSetup
            }
          }
        )}
      />

      {/* Phase Oleosa Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Fases Oleosas',
          '🛢️',
          data.phaseOleosa,
          phaseOleosaFormFields,
          phaseOleosaColumns,
          operations.handlePhaseOleosaSubmit,
          operations.handlePhaseOleosaToggle,
          operations.handlePhaseOleosaDelete,
          {
            description: 'Gestiona las fases oleosas utilizadas en sistemas OBM y SBM con propiedades y costos',
            quickSetup: {
              title: 'Carga Rápida de Fases Oleosas',
              description: 'Carga fases estándar: diesel, aceites minerales, sintéticos con densidades y precios actualizados',
              buttonLabel: '🛢️ Cargar Fases Estándar',
              onQuickSetup: handlePhaseOleosaQuickSetup
            }
          }
        )}
      />

      {/* Densificantes Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Densificantes',
          '⚖️',
          data.densificantes,
          densificanteFormFields,
          densificanteColumns,
          operations.handleDensificanteSubmit,
          operations.handleDensificanteToggle,
          operations.handleDensificanteDelete,
          {
            description: 'Administra los materiales densificantes con propiedades específicas para control de densidad',
            quickSetup: {
              title: 'Carga Rápida de Densificantes',
              description: 'Carga materiales estándar: baritina, calcita, hematita, ilmenita con densidades y costos',
              buttonLabel: '⚖️ Cargar Densificantes Estándar',
              onQuickSetup: handleDensificantesQuickSetup
            }
          }
        )}
      />

      {/* Master Quick Setup */}
      <div style={{
        marginTop: '40px',
        padding: '25px',
        backgroundColor: '#efebe9',
        borderRadius: '12px',
        border: '2px solid #795548'
      }}>
        <h3 style={{ color: '#5D4037', marginBottom: '15px' }}>🚀 Configuración Completa de Lodos</h3>
        <p style={{
          margin: '15px 0',
          fontSize: '16px',
          color: '#424242',
          lineHeight: '1.5'
        }}>
          ¿Primera vez configurando el sistema de lodos? Carga toda la información estándar de una vez:
          tipos de lodo, fases oleosas y densificantes con propiedades técnicas y costos actualizados.
        </p>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          marginTop: '20px'
        }}>
          <button
            onClick={handleAllMudDataQuickSetup}
            disabled={!userPermissions.canEditReports}
            style={{
              padding: '15px 30px',
              backgroundColor: userPermissions.canEditReports ? '#795548' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: userPermissions.canEditReports ? 'pointer' : 'not-allowed',
              fontWeight: 'bold',
              boxShadow: userPermissions.canEditReports ? '0 4px 8px rgba(121, 85, 72, 0.3)' : 'none'
            }}
          >
            🧪 Configurar Sistema Completo
          </button>

          <div style={{ fontSize: '14px', color: '#666', maxWidth: '400px' }}>
            <strong>Incluye:</strong><br/>
            • 7 tipos de lodo estándar (WBM/OBM/SBM)<br/>
            • 5 fases oleosas con precios<br/>
            • 5 densificantes con propiedades técnicas
          </div>
        </div>
      </div>
    </>
  );
};